import { Portal as RadixPortal } from '@radix-ui/react-dropdown-menu';
import clsx from 'clsx';
import { forwardRef, useContext, useState } from 'react';

import { useOverlayStacking } from '../../core/hooks/useOverlayStacking.js';
import { AriaLabelingProps } from '../../core/types/a11y-props.js';
import { DataTestId } from '../../core/types/data-props.js';
import { StylingProps } from '../../core/types/styling-props.js';
import { WithChildren } from '../../core/types/with-children.js';
import { usePopper } from '../../overlays/utils/use-popper-compat.js';
import { MenuPortalOwnerContext } from './contexts/MenuPortalOwnerContext.js';
import * as menuStyles from './Menu.sty.js';
import { ALIGN_OFFSET } from './SubContent.js';

/**
 * @public
 */
export interface MenuItemTooltipProps
  extends WithChildren, DataTestId, StylingProps, AriaLabelingProps {}

/** @internal */
export const MenuItemTooltipWrapper = ({
  triggerElement,
  children,
  onMouseEnter,
  onMouseLeave,
}: {
  triggerElement: HTMLElement | null;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
} & WithChildren) => {
  const [menuTooltipRefRef, setMenuTooltipRefRef] =
    useState<HTMLDivElement | null>(null);
  const ownerId = useContext(MenuPortalOwnerContext);

  // The menu content already has a stacking z-index from useOverlayStacking.
  // The tooltip is rendered into a sibling portal, so it needs an explicit
  // z-index to guarantee it appears above the menu panel.
  const { style: overlayStyle } = useOverlayStacking(undefined, 'overlay');

  // strategy: 'fixed' is required here because the tooltip is rendered into a
  // portal at document.body. With 'absolute', the offset parent would be body
  // (position: static), causing incorrect scroll-relative coordinates. 'fixed'
  // positions relative to the viewport, which matches the already-fixed menu
  // content that contains the trigger element.
  const { styles, attributes } = usePopper(triggerElement, menuTooltipRefRef, {
    strategy: 'fixed',
    placement: 'right-start',
    modifiers: [
      {
        name: 'offset',
        options: {
          offset: [ALIGN_OFFSET, 0],
        },
      },
      {
        name: 'flip',
        options: {
          fallbackPlacements: ['left-start'],
        },
      },
    ],
  });

  // RadixPortal (not bare createPortal) is used intentionally: it registers
  // this portal with Radix's DismissableLayer so that pointer events inside the
  // tooltip are not treated as "outside clicks" that would close the menu.
  // The tooltip must live outside the role="menu" DOM subtree to satisfy the
  // aria-required-children rule, which forbids div[tabindex] children such as
  // the CodeSnippet's focus-container wrapper.
  return (
    <RadixPortal>
      <div
        ref={setMenuTooltipRefRef}
        style={{
          ...styles.popper,
          zIndex: overlayStyle.zIndex,
          pointerEvents: 'auto',
        }}
        className={menuStyles.tooltipWrapper}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        {...attributes.popper}
        {...(ownerId ? { 'data-tooltip-menu-id': ownerId } : {})}
        aria-hidden="true"
      >
        {children}
      </div>
    </RadixPortal>
  );
};
MenuItemTooltipWrapper.displayName = 'MenuItemTooltipWrapper';

/**
 * A tooltip which can be used as a child of an Item. Its content will appear
 * in an overlay when the Item is focused.
 * @public
 */
export const ItemTooltip = forwardRef<HTMLDivElement, MenuItemTooltipProps>(
  (
    {
      children,
      className: consumerClassName,
      style: consumerStyle,
      'data-testid': dataTestId,
      'aria-label': ariaLabel,
      'aria-labelledby': ariaLabelledBy,
      'aria-describedby': ariaDescribedBy,
      'aria-details': ariaDetails,
    },
    forwardedRef,
  ) =>
    children ? (
      <div
        className={clsx(menuStyles.tooltip, consumerClassName)}
        style={consumerStyle}
        ref={forwardedRef}
        data-testid={dataTestId}
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy}
        aria-describedby={ariaDescribedBy}
        aria-details={ariaDetails}
      >
        {children}
      </div>
    ) : null,
);

(ItemTooltip as typeof ItemTooltip & { displayName: string }).displayName =
  'Menu.ItemTooltip';
